import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnnouncementDetailController } from 'fragments/announcements/interfaces';
import { useAPIAnnouncements } from 'services/announcements/api-announcements.service';
import { Announcement, AnnouncementReadStats } from 'services/announcements/announcements.service';
import { useMessenger } from 'tools/view-hooks/messenger-hook';

export const useAnnouncementDetailController = (): AnnouncementDetailController => {
    const navigate = useNavigate();
    const { id = '' } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const { getSiteAnnouncements, markAnnouncementRead, getAnnouncementReads, deleteAnnouncement } =
        useAPIAnnouncements();
    const messenger = useMessenger();

    const accessToken = searchParams.get('access_token') || '';
    const siteId = searchParams.get('site_id') || '';
    const isAdmin = searchParams.get('is_admin') === 'true';

    const [announcement, setAnnouncement] = useState<Announcement | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [stats, setStats] = useState<AnnouncementReadStats | undefined>(undefined);

    useEffect(() => {
        if (accessToken && siteId && id) {
            getSiteAnnouncements(accessToken, siteId)
                .then((announcements) => setAnnouncement(announcements.find((a) => a.id === id) || null))
                .catch(() => {
                    // el errorHandler centralizado (dentro del service) ya navega en errores de red/sesión
                })
                .finally(() => setLoading(false));
        } else {
            navigate('/mobile/404');
        }
    }, [accessToken, siteId, id]);

    useEffect(() => {
        if (accessToken && id) {
            markAnnouncementRead(accessToken, id).catch(() => {
                // best-effort: marcar como leído no bloquea la vista si falla
            });
        }
    }, [accessToken, id]);

    useEffect(() => {
        if (isAdmin && accessToken && id) {
            getAnnouncementReads(accessToken, id)
                .then(setStats)
                .catch(() => {
                    // sin stats no se muestra el bloque de acuse de lectura
                });
        }
    }, [isAdmin, accessToken, id]);

    const qs = `access_token=${accessToken}&site_id=${siteId}&is_admin=${isAdmin}`;

    const onBack = () => navigate(-1);

    const onRetire = () => {
        deleteAnnouncement(accessToken, id)
            .then(() => {
                messenger.showSuccessMessage({ key: 'announcements.retire-done' });
                navigate(`/mobile/announcements?${qs}`);
            })
            .catch(() => {
                // el errorHandler centralizado (dentro del service) ya informa el error
            });
    };

    return {
        state: { announcement, loading, isAdmin, stats },
        events: { onBack, onRetire },
    };
};
